import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/profile';
import { logger } from '@/utils/logger';

/**
 * User Service
 * Manages user profiles and preferences
 */
export class UserService {
  /**
   * Fetch user profile by ID
   */
  static async fetchProfile(userId: string): Promise<UserProfile | null> {
    try {
      logger.debug("UserService.fetchProfile starting for user:", userId);
      
      // Try to get cached profile first
      const cachedProfile = this.getCachedProfile(userId);
      if (cachedProfile) {
        logger.debug("Using cached profile:", cachedProfile.id);
        return cachedProfile;
      }
      
      logger.debug("Fetching profile from database...");
      
      // Fetch profile from database
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      logger.debug("Profile fetch result:", { data: !!data, error: error?.message });
      
      if (error) {
        // Handle profile not found case
        if (error.code === 'PGRST116') {
          logger.debug("Profile doesn't exist, creating default profile");
          return await this.createDefaultProfile(userId);
        }
        
        logger.error('Error fetching profile:', error);
        throw error;
      }
      
      if (data) {
        logger.debug("Profile fetched successfully");
        // Cache profile
        this.cacheProfile(userId, data as UserProfile);
        return data as UserProfile;
      }
      
      logger.warn("No profile data returned");
      return null;
    } catch (error) {
      logger.error('Error in fetchProfile:', error);
      return null;
    }
  }
  
  /**
   * Create default profile for new user
   */
  static async createDefaultProfile(userId: string): Promise<UserProfile | null> {
    try {
      logger.debug("Creating default profile for user:", userId);
      
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || "";
      const username = email ? email.split('@')[0] : `user_${Date.now()}`;
      
      logger.debug("Attempting direct profile creation");
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          username: username,
          display_name: username,
          bio: null,
          avatar_url: null,
          knitting_style: "not-specified",
          measurement_system: "metric",
          email_marketing: true,
          email_project_updates: true,
          email_community_updates: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();
      
      if (error) {
        logger.error('Error creating default profile:', error);
        return null;
      }
      
      let profileData: UserProfile | null = null;
      if (data && data.length > 0) {
        profileData = data[0] as UserProfile;
      }
      
      // Cache the new profile
      if (profileData) {
        this.cacheProfile(userId, profileData);
      }
      
      return profileData;
    } catch (error) {
      logger.error('Error creating default profile:', error);
      return null;
    }
  }
  
  /**
   * Update user profile
   */
  static async updateProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      // Check if update includes display_name and if it's unique
      if (profile.display_name) {
        const { data: existingUsers, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .eq('display_name', profile.display_name)
          .neq('id', userId);
          
        if (checkError) throw checkError;
        
        if (existingUsers && existingUsers.length > 0) {
          throw new Error("Display name already taken");
        }
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...profile,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select();
      
      if (error) {
        logger.error('Error updating profile:', error);
        throw error;
      }
      
      if (data && data.length > 0) {
        // Update cache with new profile data
        this.cacheProfile(userId, data[0] as UserProfile);
        return data[0] as UserProfile;
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred updating profile');
    }
  }
  
  /**
   * Upload profile avatar - deletes old avatar file to prevent storage accumulation
   */
  static async uploadAvatar(userId: string, file: File): Promise<string> {
    try {
      // First, get the current avatar URL to delete the old file
      const currentProfile = await this.fetchProfile(userId);
      const oldAvatarUrl = currentProfile?.avatar_url;
      
      // Delete old avatar file if it exists and is in our storage
      if (oldAvatarUrl && oldAvatarUrl.includes('/avatars/')) {
        try {
          // Extract file path from URL (format: .../avatars/filename.ext)
          const urlParts = oldAvatarUrl.split('/avatars/');
          if (urlParts.length > 1) {
            const oldFilePath = urlParts[1].split('?')[0]; // Remove any query params
            await supabase.storage.from('avatars').remove([oldFilePath]);
          }
        } catch (deleteError) {
          // Log but don't fail - old file cleanup is best-effort
          logger.warn('Could not delete old avatar file:', deleteError);
        }
      }
      
      const fileExt = file.name.split('.').pop();
      const filePath = `${userId}-${Math.random().toString(36).slice(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      const avatarUrl = data.publicUrl;
      
      // Update profile with new avatar URL
      await this.updateProfile(userId, { avatar_url: avatarUrl });
      
      return avatarUrl;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred uploading avatar');
    }
  }
  
  /**
   * Cache profile in session storage
   */
  static cacheProfile(userId: string, profile: UserProfile): void {
    try {
      sessionStorage.setItem(`profile-${userId}`, JSON.stringify(profile));
    } catch (error) {
      logger.error('Error caching profile:', error);
    }
  }
  
  /**
   * Get cached profile from session storage
   */
  static getCachedProfile(userId: string): UserProfile | null {
    try {
      const cachedProfile = sessionStorage.getItem(`profile-${userId}`);
      if (!cachedProfile) return null;
      
      return JSON.parse(cachedProfile) as UserProfile;
    } catch (error) {
      logger.error('Error getting cached profile:', error);
      return null;
    }
  }
  
  // NOTE: The former checkIsAdmin() method (first-user auto-admin bootstrap) was
  // removed July 2026. It counted profiles client-side and self-granted admin when
  // count === 1 — a privilege-escalation hazard once profiles RLS becomes owner-only
  // (every user would see count 1). Admin status is determined by the user_roles
  // table via useAuthState / the is_admin() database function.
}
